/**
 * 実績トレンド反映 集計窓 W の状態管理 hook (demand-trend-window-variable
 * design D2/D3/D4)。
 *
 * - W の初期値・保存は端末 localStorage (models/trendWindowStorage.ts, D4)。
 * - GetDemandForecastDaily は既定 6 コマの trend_preview を返す (Contract)。
 *   保持 W が既定と異なる場合は、初回に一度だけ PreviewTrendReflection を
 *   呼んで即座に W に追従させる。
 * - W をユーザーが変更したら debounce 後に PreviewTrendReflection を呼び直す
 *   (D2: 専用 RPC で avgRatio / corrected_slots を再計算)。
 * - 返す `hasFetched` が false の間は呼び出し元は GetPage の trend_preview
 *   (既定窓) を使う。true になったら `trendPreview` (undefined を含む — 新しい
 *   窓で有効実績コマ2件未満なら absent が正しい結果) に差し替える。
 *   `trendPreview` フィールド単体を「未取得 = undefined」の目印に使うと、
 *   再計算後の正当な absent 結果を「まだ取得していない」と誤認して GetPage の
 *   古い値へ fall back してしまうため、取得済みかどうかは別 flag で持つ。
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { useMutation } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { previewTrendReflection } from '@ebalance/proto/bffuser/v1/demand_forecast_service-DemandForecastService_connectquery'
import {
  PreviewTrendReflectionRequestSchema,
  type TrendReflectionPreview,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import {
  TREND_WINDOW_KOMA_DEFAULT,
  clampTrendWindowKoma,
  loadTrendWindowKoma,
  saveTrendWindowKoma,
} from '~/pages/DemandForecastDaily/models/trendWindowStorage'

/** W 変更後、 RPC を呼ぶまでの debounce (連続入力を1 mutation に集約)。 */
const WINDOW_CHANGE_DEBOUNCE_MS = 400

type Params = {
  caseId: string | undefined
  areaCode: AreaCode | undefined
  targetDate: ProtoDate | undefined
  /**
   * 対象日=today かつページ読込済み。既定 6 コマ窓の GetDemandForecastDaily
   * 結果 (trend_preview) が absent かどうかには依存しない — 保存済み W≠6 が
   * 既定窓では absent でも preview を返せることがあるため、既定窓の有無に
   * 関わらず本 RPC は発火させる (PR #5728 レビュー第2ラウンド指摘)。
   * 呼び出し元の表示 gate (`hasTrendReflect`) は本 `enabled` とは別物で、
   * 「最終的に採用した trendPreview の有無」で決まる (= 本 gate の結果として
   * 見えるので直接一致しない)。
   */
  enabled: boolean
}

type Result = {
  windowKoma: number
  /** W 入力の変更ハンドラ (クランプ + 端末保存 + debounce 後の再計算を内包)。 */
  onWindowKomaChange: (raw: number) => void
  /** true = 本 hook が少なくとも 1 回 PreviewTrendReflection の応答を受け取った
   * (= `trendPreview` を信頼してよい)。false の間は呼び出し元は GetPage の
   * trend_preview (既定窓) を使う。 */
  hasFetched: boolean
  /** 本 hook が再計算した最新の trend_preview (`hasFetched` が true の時のみ有効。
   * undefined は「その窓で有効実績コマ2件未満」の正当な absent 結果もありうる)。 */
  trendPreview: TrendReflectionPreview | undefined
}

export function useTrendReflectionWindow({
  caseId,
  areaCode,
  targetDate,
  enabled,
}: Params): Result {
  const [windowKoma, setWindowKoma] = useState<number>(() =>
    loadTrendWindowKoma(),
  )
  const [trendPreview, setTrendPreview] = useState<
    TrendReflectionPreview | undefined
  >(undefined)
  const [hasFetched, setHasFetched] = useState(false)
  const mutation = useMutation(previewTrendReflection)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // caseId が変わったら (別ケースへ遷移) 初回追従判定と再計算結果をリセットする。
  const lastCaseIdRef = useRef<string | undefined>(undefined)
  const didInitialSyncRef = useRef(false)

  const fetchPreview = useCallback(
    (koma: number) => {
      if (
        !enabled ||
        caseId === undefined ||
        areaCode === undefined ||
        targetDate === undefined
      )
        return
      mutation.mutate(
        create(PreviewTrendReflectionRequestSchema, {
          caseId,
          areaCode,
          targetDate,
          aggregationWindowKoma: koma,
        }),
        {
          onSuccess: (resp) => {
            setTrendPreview(resp.trendPreview)
            setHasFetched(true)
          },
        },
      )
    },
    // mutation は connect-query の useMutation が返す安定 object ではないため
    // deps に含めない (mutate 自体は同一 RPC 定義に対して stable)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, caseId, areaCode, targetDate],
  )

  useEffect(() => {
    if (lastCaseIdRef.current !== caseId) {
      lastCaseIdRef.current = caseId
      didInitialSyncRef.current = false
      setTrendPreview(undefined)
      setHasFetched(false)
    }
  }, [caseId])

  // 初期表示: 保持 W が既定 (6コマ) と異なる場合のみ、 GetPage の既定計算値を
  // 専用 RPC の結果で即座に上書きする (Contract「初期表示は既定、frontend が
  // 保持 W≠6 のとき本 RPC で追従」)。既定のままなら GetPage の trend_preview で
  // 足りるため呼ばない (wasteful round-trip を避ける)。
  useEffect(() => {
    if (didInitialSyncRef.current || !enabled) return
    didInitialSyncRef.current = true
    if (windowKoma !== TREND_WINDOW_KOMA_DEFAULT) {
      fetchPreview(windowKoma)
    }
  }, [enabled, windowKoma, fetchPreview])

  useEffect(
    () => () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current)
    },
    [],
  )

  const onWindowKomaChange = useCallback(
    (raw: number) => {
      const clamped = clampTrendWindowKoma(raw)
      setWindowKoma(clamped)
      saveTrendWindowKoma(clamped)
      if (debounceRef.current !== null) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        fetchPreview(clamped)
      }, WINDOW_CHANGE_DEBOUNCE_MS)
    },
    [fetchPreview],
  )

  return { windowKoma, onWindowKomaChange, hasFetched, trendPreview }
}
